import { supabase } from './supabase';

// Client side of account deletion. Both calls go to Netlify functions rather
// than to the database directly: `authenticated` has SELECT on profiles and
// nothing else, so deletion_requested_at can only be written server-side.
//
// The uid is never sent. The functions read it from the access token, which is
// the only thing that decides whose account is affected.

const GRACE_DAYS = 30;

// Everything this app writes to the device. Cleared when deletion is requested
// so the browser stops carrying the account's traces around during the grace
// window — the DB rows are what the 30 days are for, not this cache.
//
// The last two are declared next to the code that writes them; the literals are
// repeated here deliberately rather than exported from a component and a util,
// which would drag SettingsTab and geoEvents into this module.
const LOCAL_KEYS = (userId) => [
  'si_academy_done',
  'si_academy_quiz',
  'si_lang',
  'investorProfile',                // PROFILE_KEY  — OnboardingFlow.jsx:7
  'si_geo_classify_count',          // CAP_KEY      — utils/geoEvents.js
  'si_mark_invite_dismissed',       // DISMISS_KEY  — MarkInviteBanner.jsx
  userId ? `si_avatar_url_${userId}` : null,   // AVATAR_LS_KEY — SettingsTab.jsx
].filter(Boolean);

export function clearLocalCache(userId) {
  for (const key of LOCAL_KEYS(userId)) {
    try { localStorage.removeItem(key); } catch { /* private mode */ }
  }
}

async function post(path) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error('no session');

  const res = await fetch(`/.netlify/functions/${path}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });

  let body = null;
  try { body = await res.json(); } catch { /* empty or non-JSON */ }
  return { ok: res.ok, status: res.status, body };
}

// Marks the account and ends the session server-side. Returns { ok, status }.
// The caller clears the local cache and signs out on ok.
export async function requestAccountDeletion(userId) {
  const res = await post('account-delete-request');
  if (res.ok) clearLocalCache(userId);
  else console.error('[account] delete request →', res.status, res.body?.error);
  return res;
}

// Clears the mark. 409 means the window has closed.
export async function restoreAccount() {
  const res = await post('account-restore');
  if (!res.ok) console.error('[account] restore →', res.status, res.body?.error);
  return res;
}

// Reads the mark for the signed-in user. `authenticated` may SELECT its own
// profiles row, so this needs no function call.
//
// Returns an ISO string, or null when the account is active. Throws only if the
// caller wants to distinguish "unknown" — a read failure returns null so a
// transient network error cannot lock somebody out of their own app.
export async function fetchDeletionState(userId) {
  if (!supabase || !userId) return null;
  const { data, error } = await supabase
    .from('profiles')
    .select('deletion_requested_at')
    .eq('id', userId)
    .maybeSingle();
  if (error) {
    console.error('[account] deletion state →', error.code, error.message);
    return null;
  }
  return data?.deletion_requested_at ?? null;
}

// Facts derived from the mark, for the screens to render. No wording here.
export function deletionSchedule(requestedAtIso) {
  const requestedMs = Date.parse(requestedAtIso);
  if (!Number.isFinite(requestedMs)) return null;
  const deletionMs = requestedMs + GRACE_DAYS * 24 * 60 * 60 * 1000;
  return {
    requestedAt: new Date(requestedMs),
    deletionAt: new Date(deletionMs),
    daysLeft: Math.max(0, Math.ceil((deletionMs - Date.now()) / 86_400_000)),
  };
}

// dd.mm.yyyy — the app's date form, and the one the confirmation and the grace
// screen both quote.
export function formatDate(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
}

export { GRACE_DAYS };
